// PÉNZÜGY — Bevétel/kiadás. A menü-csoport utolsó pontja, és a cég pénzügyi képének HÁROM olvasata egy lapon:
//
//   1. ÖSSZEGZÉS — mennyi jön be és mennyi megy ki ebben a hónapban (a nyitó kérdés),
//   2. BEVÉTEL-ELŐREJELZÉS — a legacy CRM RevenueForecast 1:1 portja, változatlanul,
//   3. KIADÁSOK — a NAV-ból importált és a kézzel rögzített költségek, besorolással.
//
// A lap CSAK héj: a fülek maguk `{ toolbar, content }`-et adó hookok (`features/finance/view.ts`), a hónap
// pedig ITT lakik — így a fül-váltás nem rántja vissza a hónapot, és az `AppShell` eszköztár-sávja fülönként
// a saját vezérlőit mutatja. A rejtett fül lekérdezése el sem indul (`enabled`), tehát a fül-váltogatás nem
// terheli az Odin kérés-plafonját.
//
// Page-gate: a Bevétel VAGY a Kiadás jog (user-döntés: a kettő KÜLÖN védett jog). Csak Bevétellel az Összegzés
// (csak a Bevétel-elemzés) + a Bevételek fül látszik, csak Kiadással az Összegzés (csak a Kiadás-elemzés) + a
// Kiadások fül; az Áttekintés mindkettőt kéri. A jogok: `lib/use-finance-access.ts`.

import {
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
  PresentationChartLineIcon,
} from '@heroicons/react/24/outline';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { AppShell } from '@/components/shared/app-shell';
import { EmptyState } from '@/components/shared/empty-state';
import { PageBody } from '@/components/shared/page-body';
import { PageNotices } from '@/components/shared/page-notices';
import { Segmented } from '@/components/ui/segmented';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useExpensesView } from '@/features/expenses/use-expenses-view';
import { useSummaryView } from '@/features/finance/use-summary-view';
import { useForecastView } from '@/features/revenue-forecast/components/forecast-view';
import { useRevenueListView } from '@/features/revenue-forecast/components/revenue-list-view';
import { useListFilters } from '@/hooks/use-list-filters';
import { requireAuth } from '@/lib/auth';
import { currentMonth } from '@/lib/month';
import type { TabView } from '@/lib/tab-view';
import { useFinanceAccess } from '@/lib/use-finance-access';

/**
 * A LAP ÁLLAPOTA AZ URL-BEN — fül, hónap és MINDKÉT fül szűrői (a repo state-konvenciója: a szűrt nézet legyen
 * linkelhető és F5-biztos). A route-nak ISMERNIE kell a kulcsokat, különben a router kiszűri őket.
 *
 * MIÉRT PREFIXES a szűrő-kulcs (`exp_` / `rev_`): a lapon HÁROM fül ül egy címen, és a kiadás- meg a
 * bevétel-oldalnak egyaránt van keresője — egy közös `q` a szállító-keresést átvinné a bevétel-szűrőbe.
 * A fül és a hónap prefix NÉLKÜL marad: azok a lap KÖZÖS dimenziói (a fül-váltás nem rántja vissza a hónapot).
 *
 * MIÉRT marad mindkét fül szűrője az URL-ben: a fül-váltás nem dobhatja el a másik fülön beállított szűrést —
 * oda-vissza kattintva különben elveszne a munka.
 */
const financeSearchSchema = z.object({
  tab: z.string().optional(),
  month: z.string().optional(),
  /** Az Összegzés AL-NÉZETE (áttekintés · bevétel · kiadás) — a lap közös dimenziója, ezért prefix nélkül. */
  view: z.string().optional(),
  // Kiadás-fül
  //
  // ⚠️ MINDEN kulcsnak itt KELL lennie, amit a fül használ: a `useSearch` a VALIDÁLT keresést adja vissza, és
  // a `z.object()` a nem ismert kulcsot NÉMÁN eldobja — a szűrő ilyenkor az URL-be még beíródik, de olvasni
  // már nem lehet, tehát NEM HAT és újratöltésre eltűnik. (Mérve: a köteg-szűrő az URL-ben ott volt, a lista
  // mégis az egész hónapot mutatta; ugyanez állt a kibocsátó- és a rendezés-kulcsokra is, csak ott nem
  // tűnt fel.)
  exp_q: z.string().optional(),
  exp_category: z.string().optional(),
  exp_source: z.string().optional(),
  exp_issuer: z.string().optional(),
  exp_dismissed: z.string().optional(),
  exp_sort: z.string().optional(),
  exp_order: z.string().optional(),
  /** Egy import-KÖTEG sorai — az import jelentéséből ide navigál a felület. */
  exp_batch: z.string().optional(),
  /** Teendő-szűrő: bér-típusú tételen ülő sor munkatárs nélkül. */
  exp_missing_employee: z.string().optional(),
  /** Teendő-szűrő: a még pótolatlan összegű sorok (az összegző „összeg nélküli” számlálója). */
  exp_missing_amount: z.string().optional(),
  // Bevétel-előrejelzés fül (a multi-szűrők vesszős listák — `parseListParam`)
  rev_q: z.string().optional(),
  rev_status: z.string().optional(),
  /** Díjtípus-szűrő (`recurring` | `one_time`; hiány → mind). */
  rev_billing: z.string().optional(),
  rev_main: z.string().optional(),
  rev_sub: z.string().optional(),
  rev_service: z.string().optional(),
  rev_plan: z.string().optional(),
  rev_client: z.string().optional(),
  rev_account: z.string().optional(),
  /** A KIADÁS-ELEMZÉS szűrői (`xa_` — a Kiadások fül `exp_` szűrőitől független). */
  xa_q: z.string().optional(),
  xa_type: z.string().optional(),
  xa_cat: z.string().optional(),
  xa_issuer: z.string().optional(),
  xa_source: z.string().optional(),
});

export const Route = createFileRoute('/finance')({
  validateSearch: (search) => financeSearchSchema.parse(search),
  beforeLoad: () => requireAuth(),
  component: FinancePage,
});

type FinanceTab = 'summary' | 'revenue' | 'expenses';

const TABS: readonly FinanceTab[] = ['summary', 'revenue', 'expenses'];
/**
 * A fül feloldása az URL-ből. A RÉGI `?tab=forecast` link a BEVÉTELEK fülre visz: a korábbi
 * „Bevétel-előrejelzés" fül KIMUTATÁSA az Összegzésre költözött, a néven pedig a lista maradt — egy
 * mentett link így nem visz üres lapra.
 */
const isTab = (v: string): v is FinanceTab => (TABS as readonly string[]).includes(v);
const resolveTab = (v: string): FinanceTab =>
  v === 'forecast' ? 'revenue' : isTab(v) ? v : 'summary';

/**
 * Az ÖSSZEGZÉS al-nézetei. Minden kimutatás és grafikon az Összegzésen él (user-döntés), de egy végtelen lap
 * helyett három nézetben: a vezetői ÁTTEKINTÉS (kártyák + 12 hónapos bevétel–kiadás), a BEVÉTEL kimutatása a
 * saját szűrőivel, és a KIADÁS grafikonjai. A Bevételek / Kiadások fül marad munka-lista.
 */
type SummarySubView = 'overview' | 'revenue' | 'expenses';
const SUMMARY_VIEWS: readonly SummarySubView[] = ['overview', 'revenue', 'expenses'];
const resolveSummaryView = (v: string): SummarySubView =>
  (SUMMARY_VIEWS as readonly string[]).includes(v) ? (v as SummarySubView) : 'overview';

/** Exportált a route-teszt kedvéért (a `usage-data` mintája) — a route a `component`-en át használja. */
export function FinancePage(): JSX.Element {
  const { t } = useTranslation();
  const access = useFinanceAccess();
  const permsLoading = access.isLoading;
  const gated = !permsLoading && !access.any;
  // a látható fülek és al-nézetek a jogok szerint (a tiltott fül nem csak rejtett: az URL-ből sem nyitható)
  const allowedTabs: FinanceTab[] = [
    'summary',
    ...(access.revenue ? (['revenue'] as const) : []),
    ...(access.expenses ? (['expenses'] as const) : []),
  ];
  const allowedViews: SummarySubView[] = [
    ...(access.revenue && access.expenses ? (['overview'] as const) : []),
    ...(access.revenue ? (['revenue'] as const) : []),
    ...(access.expenses ? (['expenses'] as const) : []),
  ];

  // A fül és a hónap az URL-ben. A `defaults` KULCS-KÉSZLET is egyben, ezért stabil referenciának kell lennie
  // (`useMemo`) — a `currentMonth()` a lap megnyitásakor dől el, és defaultként ki is esik az URL-ből.
  const shellDefaults = useMemo(
    () => ({ tab: 'summary', month: currentMonth(), view: 'overview' }),
    [],
  );
  const { filters: shell, setFilters: setShell } = useListFilters(shellDefaults);
  const requestedTab = resolveTab(shell.tab);
  const tab: FinanceTab = allowedTabs.includes(requestedTab) ? requestedTab : 'summary';
  const month = shell.month;
  const requestedView = resolveSummaryView(shell.view);
  const summaryView: SummarySubView = allowedViews.includes(requestedView)
    ? requestedView
    : (allowedViews[0] ?? 'overview');
  const setMonth = useCallback((m: string) => setShell({ month: m }), [setShell]);

  // MINDHÁROM hook MINDIG lefut (a hook-sorrend nem lehet feltételes) — a lekérdezéseiket az `enabled`
  // kapcsolja, tehát a rejtett fül nem kér adatot.
  const active = !gated && !permsLoading;
  // A KIMUTATÁS (kártyák, chartok, drilldown) az ÖSSZEGZÉS fülé — user-döntés: a Bevételek fül LISTA lett,
  // a Kiadások párja, az elemzés pedig egy helyre került az összegző számokkal.
  //
  // A SORREND KÖTÖTT: a kimutatás mondja meg, van-e aktív szűkítés, és az összkép-kártyáknak EZT kell
  // tudniuk — azok ugyanis SZÁNDÉKOSAN a hónap teljes halmazát mutatják, és ezt ki kell mondaniuk, különben
  // szűréskor „beragadt" számnak látszanak (mérve: a kereső a kimutatást átírja, a kártyát nem).
  // Csak a SAJÁT al-nézetén kérdez: a kimutatás a szűrőivel külön busz-kört indíthat, az áttekintés pedig a
  // szűretlen alapot a saját (azonos cache-kulcsú) lekérdezéséből kapja.
  const forecast = useForecastView({
    enabled: active && tab === 'summary' && summaryView === 'revenue',
    month,
    onMonthChange: setMonth,
  });
  const summary = useSummaryView({
    enabled: active && tab === 'summary' && summaryView !== 'revenue',
    month,
    onMonthChange: setMonth,
    narrowed: forecast.hasNarrowing,
    expenseView: summaryView === 'expenses',
  });
  const revenue = useRevenueListView({
    enabled: active && tab === 'revenue',
    month,
    onMonthChange: setMonth,
  });
  const expenses = useExpensesView({
    enabled: active && tab === 'expenses',
    month,
    onMonthChange: setMonth,
  });

  if (gated) {
    return (
      <AppShell>
        <PageBody align="left">
          <EmptyState
            title={t('finance.forbiddenTitle')}
            description={t('finance.forbiddenDesc')}
          />
        </PageBody>
      </AppShell>
    );
  }

  /**
   * Az ÖSSZEGZÉS al-nézetének NÉZETE. A BEVÉTEL a kimutatás (a hónap-léptető mellett a kereső és a teljes
   * szűrő-készlet, a bevétel-oldali jelzésekkel); az ÁTTEKINTÉS és a KIADÁS a saját hook blokkjai, a hónap-
   * léptetővel és a kiadás-oldali jelzésekkel (azok a kiadás- és az egyenleg-számot minősítik).
   */
  const summaryTab: TabView =
    summaryView === 'revenue'
      ? forecast
      : {
          // a Kiadás-elemzés saját szűrő-sávot kap (kereső + szűrők), az Áttekintés a hónap-léptetőt
          toolbar: summaryView === 'expenses' ? summary.expenseToolbar : summary.toolbar,
          // az ÁTTEKINTÉS bevétel-száma is a kimutatásból jön → a deviza-szabály ott is kimondva (a Kiadás nem kéri)
          notices:
            summaryView === 'overview'
              ? [
                  ...(summary.notices ?? []),
                  { key: 'currency', tone: 'neutral', title: t('revenueForecast.currencyNote') },
                  { key: 'period-rule', tone: 'neutral', title: t('revenueForecast.periodRule') },
                ]
              : (summary.notices ?? []),
          content: summaryView === 'expenses' ? summary.expenseDetails : summary.content,
        };
  /**
   * Az AL-NÉZET kapcsoló a FŐ fülsor ALATT, a figyelmeztető sávok FÖLÖTT (user-döntés): a jelzések a kiválasztott
   * nézethez tartoznak, tehát a nézet-választás áll előttük — a kapcsoló nem ugrál aszerint, hány jelzés van.
   */
  const summaryNav =
    tab === 'summary' ? (
      <div className="px-4 pt-2">
        <Segmented
          aria-label={t('finance.summaryViews.label')}
          value={summaryView}
          onChange={(v) => setShell({ view: v })}
          options={allowedViews.map((v) => ({
            value: v,
            label: t(`finance.summaryViews.${v}`),
          }))}
        />
      </div>
    ) : null;
  const view: TabView = tab === 'summary' ? summaryTab : tab === 'revenue' ? revenue : expenses;

  return (
    <AppShell
      // A mobil FAB elsődleges akciója az ÉPP AKTÍV fülé (a két olvasó fül nem ajánl akciót).
      {...(view.primaryAction ? { primaryAction: view.primaryAction } : {})}
      {...(view.secondaryActions ? { secondaryActions: view.secondaryActions } : {})}
      toolbar={
        // A FÜL-VÁLTÓ a fül saját eszköztára FÖLÖTT (a fejlesztések-oldal mintája): a sáv így sosem tördel,
        // és a fülön belüli szűrők ott maradnak, ahol a modul többi lapján megszoktuk.
        // AZ IKONOK: a `Squares2X2` az Áttekintés menüponté, a `ReceiptPercent` a Számláké volt — két
        // különböző CÉLRA ugyanaz a jel félrevezet. Az Összegzés a korábbi bevétel-előrejelzés grafikon-ikonját
        // kapta (az sehol máshol nem szerepel), a másik kettő pedig a PÉNZ IRÁNYÁT mondja — ugyanazzal a fel-/
        // le-trend jellel, amit az Összegzés fül Bevétel/Kiadás kártyája is visel: a fül és a kártya egy nyelvet
        // beszél.
        <div className="flex flex-col">
          <div className="px-4 pt-2">
            <Tabs value={tab} onValueChange={(v) => setShell({ tab: v })}>
              <TabsList>
                <TabsTrigger value="summary">
                  <PresentationChartLineIcon />
                  {t('finance.tabs.summary')}
                </TabsTrigger>
                {access.revenue && (
                  <TabsTrigger value="revenue">
                    <ArrowTrendingUpIcon />
                    {t('finance.tabs.revenue')}
                  </TabsTrigger>
                )}
                {access.expenses && (
                  <TabsTrigger value="expenses">
                    <ArrowTrendingDownIcon />
                    {t('finance.tabs.expenses')}
                  </TabsTrigger>
                )}
              </TabsList>
            </Tabs>
          </div>
          {/* FIGYELMEZTETŐ SÁVOK — a SZŰRŐ-SOR ALATT, egy lenyitható „Információk" sorban, alapból összecsukva
              (`PageNotices`). Korábban a fül-sáv és a kereső KÖZÖTT, nyitva álltak, és minden lapnyitáskor
              lejjebb tolták a lap tetejét (user-lelet). A sáv-blokk része maradt: görgetés közben is látszik.
              A zárt sor tónusonként darabszámot és pöttyöt mutat, így egy alulbecslés lenyitás nélkül is jelez. */}
          {summaryNav}
          {view.toolbar}
          {view.notices && <PageNotices notices={view.notices} className="px-4 pb-2" />}
        </div>
      }
    >
      {/* A FIGYELMEZTETŐ SÁVOK — EGY blokkban, a sáv-blokk alján (a szűrő-sor alatt), a tartalom ELŐTT.
          Korábban minden hook a saját blokkjában hozta a sajátjait, ezért a kártya-sorok KÖZÉ és a lap
          ALJÁRA is jutott belőlük — a lap alján álló „a kiadás-végösszeg alulbecsül" sáv pont attól a
          számtól volt két képernyőnyire, amit minősített. A vízszintes térköz a lista-lapok `px-4`-e. */}
      {/* A FÜL HOZZA A SAJÁT ELRENDEZÉSÉT (`TabView` szerződés, `services.tsx` mintája): a lista-fül a
          lista-lapok `px-4 pb-2` dobozát, a két olvasó fül a `PageBody`-t. Héj-szintű `PageBody` esetén a
          lista 20/24px-cel lejjebb indult, mint MINDEN más lista-lapon (mérve 375/1280-on: 214 vs 194). */}
      {view.content}
    </AppShell>
  );
}
